import cx from 'classnames';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
    useExperimentVariation,
    useTrackEventAmplitude,
} from '../../../../../core/Tracking/Amplitude/Amplitude';
import { IconSearch } from '../../../../../design-system/components/icons/Search';
import { DotsLoader } from '../../../../../design-system/components/loader/dots/DotsLoader';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import { useGetCountryAndPlaceSearch } from '../../../../../resources/countryAndPlaceSearch/api/pyrite/countryAndPlaceSearch';
import useDebounce from '../../../../../shared/hooks/useDebounce';
import { useLocale } from '../../../../../shared/hooks/useLocale';
import { useOutsideClick } from '../../../../../shared/hooks/useOutsideClick';
import { useUrlGenerator } from '../../../../../shared/hooks/useUrlGenerator';

import style from './SearchBar.module.css';

export function SearchBar() {
    const { trackEvent } = useTrackEventAmplitude();
    const { locale } = useLocale();

    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [resultActive, setResultActive] = useState<number | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const firstResultRef = useRef<HTMLAnchorElement>(null);
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();

    const ABSearchRecommendation = useExperimentVariation('fr_hp_search_reco');

    const isABSearchRecommendation = ABSearchRecommendation?.value === 'variation';

    const debouncedSearchQuery = useDebounce(searchQuery, 200);
    const isSearchQueryValid = debouncedSearchQuery.length >= 1;

    const { data: searchResults, isLoading } = useGetCountryAndPlaceSearch({
        locale,
        query: debouncedSearchQuery,
        options: {
            enabled: isSearchQueryValid,
        },
    });

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setSearchQuery(value);
    };

    const handleFocus = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.searchbarUsed({
                ...defaultProperties,
                block: 'cover',
                button_clicked: false,
                scrolling_menu_clicked: false,
                search_value: searchQuery,
            });
        });
        setIsDropdownVisible(true);
    };

    useOutsideClick(containerRef, () => {
        setIsDropdownVisible(false);
        setResultActive(null);
    });

    const hasResults = searchResults && searchResults.length > 0;
    const hasDestinationResult = hasResults && searchResults[0].pageType === 'destination_page';

    useEffect(() => {
        function listener() {
            const element = document.activeElement;
            if (!element) return;
            if (element.id === 'home-cover-search-input') {
                window.setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 0);
            }
        }

        window.addEventListener('resize', listener);

        return () => {
            window.removeEventListener('resize', listener);
        };
    }, []);

    return (
        <div className={style.searchContainer} ref={containerRef}>
            <div className={style.searchbar}>
                <div className={style.searchInput}>
                    <div className={style.searchIcon}>
                        <IconSearch />
                    </div>
                    <input
                        id="home-cover-search-input"
                        type="text"
                        placeholder={formatMessage({ id: 'page.home.searchPlaceholder' })}
                        className={style.input}
                        value={searchQuery}
                        autoComplete="off"
                        onChange={handleInputChange}
                        data-form-type="other"
                        onFocus={handleFocus}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                if (isLoading) return;

                                if (hasDestinationResult && isABSearchRecommendation) {
                                    window.location.href = generatePath('suggestions', {
                                        destinationSlug: searchResults[0].slug,
                                    });
                                } else if (hasResults) {
                                    window.location.href = searchResults[0].url;
                                } else {
                                    window.location.href = generatePath('destination_list');
                                }
                                setSearchQuery('');
                                setResultActive(null);
                            }
                        }}
                    />
                </div>
            </div>

            {isSearchQueryValid && isDropdownVisible ? (
                <div className={cx(style.dropdownContent, { [style.isLoading]: isLoading })}>
                    {isLoading ? <DotsLoader /> : null}

                    {!isLoading && !hasResults ? (
                        <Typography
                            as="a"
                            scale="primary-xs-regular"
                            href={generatePath('destination_list')}
                            className={cx(style.dropdownItem, {
                                [style.active]: resultActive === null,
                            })}
                        >
                            {formatMessage({
                                id: 'page.home.destinations.worldPage',
                            })}
                        </Typography>
                    ) : null}

                    {!isLoading && hasResults
                        ? searchResults.map((result, index) => (
                              <Typography
                                  as="a"
                                  key={result.id}
                                  ref={index === 0 ? firstResultRef : null}
                                  scale="primary-xs-regular"
                                  href={
                                      isABSearchRecommendation &&
                                      result.pageType === 'destination_page'
                                          ? generatePath('suggestions', {
                                                destinationSlug: result.slug,
                                            })
                                          : result.url
                                  }
                                  className={cx(style.dropdownItem, {
                                      [style.active]:
                                          resultActive === index ||
                                          (resultActive === null && index === 0),
                                  })}
                                  onClick={() => {
                                      setSearchQuery('');
                                      setResultActive(null);
                                      trackEvent((ampli, defaultProperties) => {
                                          ampli.searchbarUsed({
                                              ...defaultProperties,
                                              block: 'cover',
                                              button_clicked: false,
                                              scrolling_menu_clicked: true,
                                              search_value: result.name,
                                          });
                                      });
                                  }}
                                  onFocus={() => {
                                      setResultActive(index);
                                  }}
                                  onMouseEnter={(event) => {
                                      event.currentTarget.focus();
                                  }}
                                  onMouseLeave={(event) => {
                                      event.currentTarget.blur();
                                      setResultActive(null);
                                  }}
                                  onTouchStart={(event) => {
                                      event.currentTarget.focus();
                                  }}
                                  onBlur={() => {
                                      setResultActive(null);
                                  }}
                              >
                                  <span dangerouslySetInnerHTML={{ __html: result.name }} />
                              </Typography>
                          ))
                        : null}
                </div>
            ) : null}
        </div>
    );
}
